import { useState, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { SectionData } from './usePdfExportStateV2';

export interface PageSectionItem {
  sectionId: string;
  offsetY: number;
  height: number;
  isOverflowing?: boolean;
  userOffset?: number; // Track user's manual offset
  // Section splitting support
  clipTop?: number;      // How much to clip from top (for continuations)
  visibleHeight?: number; // How much of section is visible on this page
  isContinuation?: boolean; // True if this continues from previous page
}

export interface PageAssignment {
  pageNumber: number;
  sections: PageSectionItem[];
  usedHeight: number;
}

interface UseTwoPhaseRenderProps {
  sections: SectionData[];
  pageHeight: number;
  headerHeight?: number;
  pageBreaksAfter: Set<string>;
  pageBreaksBefore: Set<string>;
  sectionOffsets: Map<string, number>;
}

interface UseTwoPhaseRenderReturn {
  phase: 'measuring' | 'paginated';
  heights: Map<string, number>;
  pageAssignments: PageAssignment[];
  measureRef: React.RefObject<HTMLDivElement>;
  remeasure: () => void;
}

// Buffer for footer and safety margin
const FOOTER_BUFFER = 40;

/**
 * When splitting an oversized section across pages, snap the pixel cut
 * BACKWARD to the nearest "natural" break point inside the section's DOM
 * (a direct child of .section-content, or a <tr> inside a table). That way
 * page breaks land between paragraphs, between finding cards, or between
 * table rows — never mid-sentence or mid-row.
 *
 * Fallback: if snapping would consume less than this fraction of the page,
 * we don't snap — better to accept a pixel cut than print a half-empty page.
 */
const MIN_PAGE_FILL_TO_SNAP = 0.35;

function calculatePageAssignments(
  sections: SectionData[],
  heights: Map<string, number>,
  pageHeight: number,
  headerHeight: number,
  pageBreaksAfter: Set<string>,
  pageBreaksBefore: Set<string>,
  sectionOffsets: Map<string, number>,
  /**
   * Per-section list of offsets (in pixels from the section's top) where
   * splitting is safe. Empty list = no natural breaks known → fall back
   * to pixel-cut behavior.
   */
  breakpoints: Map<string, number[]> = new Map()
): PageAssignment[] {
  const pages: PageAssignment[] = [];
  // Account for footer space on all pages
  const effectivePageHeight = pageHeight - FOOTER_BUFFER;

  let currentPage: PageAssignment = {
    pageNumber: 1,
    sections: [],
    usedHeight: headerHeight
  };

  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    if (!section.isVisible) continue;

    const sectionHeight = heights.get(section.id) || 0;
    if (sectionHeight === 0) continue;

    const userOffset = sectionOffsets.get(section.id) || 0;

    // Calculate available height for this page (using effective height with footer buffer)
    const availableHeight = currentPage.pageNumber === 1
      ? effectivePageHeight - headerHeight
      : effectivePageHeight;

    // Check for manual page break BEFORE this section
    if (pageBreaksBefore.has(section.id) && currentPage.sections.length > 0) {
      pages.push(currentPage);
      currentPage = {
        pageNumber: pages.length + 1,
        sections: [],
        usedHeight: 0
      };
    }

    // Check for manual page break AFTER the previous section
    const prevSection = currentPage.sections[currentPage.sections.length - 1];
    if (prevSection && pageBreaksAfter.has(prevSection.sectionId)) {
      pages.push(currentPage);
      currentPage = {
        pageNumber: pages.length + 1,
        sections: [],
        usedHeight: 0
      };
    }

    // Apply positive offset as extra spacing before the section
    // This pushes the section down, potentially to the next page
    const effectiveSpaceBefore = Math.max(0, userOffset);
    const totalSpaceNeeded = sectionHeight + effectiveSpaceBefore;

    const spaceRemaining = availableHeight - currentPage.usedHeight;

    // Does section fit on current page (with its offset)?
    if (totalSpaceNeeded <= spaceRemaining) {
      // YES: Add to current page with offset
      currentPage.sections.push({
        sectionId: section.id,
        offsetY: currentPage.usedHeight + effectiveSpaceBefore,
        height: sectionHeight,
        visibleHeight: sectionHeight,
        clipTop: 0,
        userOffset: userOffset
      });
      currentPage.usedHeight += totalSpaceNeeded;
    } else {
      // Section doesn't fit - need to split it across pages
      let remainingHeight = sectionHeight;
      let clipTop = 0;
      let isFirst = true;

      // If there's content on current page, start section on new page
      if (currentPage.sections.length > 0) {
        pages.push(currentPage);
        currentPage = {
          pageNumber: pages.length + 1,
          sections: [],
          usedHeight: 0
        };
      }

      // Pre-collected natural break offsets within this section, if any.
      const sectionBreaks = breakpoints.get(section.id) || [];

      // Split section across as many pages as needed
      while (remainingHeight > 0) {
        const pageAvailableHeight = currentPage.pageNumber === 1
          ? effectivePageHeight - headerHeight
          : effectivePageHeight;

        // Apply user offset only on the first portion
        const offsetForThisPortion = isFirst ? effectiveSpaceBefore : 0;
        const spaceOnPage = pageAvailableHeight - currentPage.usedHeight - offsetForThisPortion;
        let visibleOnThisPage = Math.min(remainingHeight, Math.max(0, spaceOnPage));

        // === Natural-break snapping ===
        // The would-be cut hits the section at offset (clipTop + visibleOnThisPage).
        // Snap that offset BACKWARD to the largest known break point ≤ the cut,
        // but only if doing so still fills at least MIN_PAGE_FILL_TO_SNAP of
        // the available page. Otherwise the cut sticks and we accept it on
        // this page (better than printing a near-empty page).
        if (sectionBreaks.length > 0 && remainingHeight > visibleOnThisPage) {
          const proposedEnd = clipTop + visibleOnThisPage;
          // Largest break ≤ proposedEnd AND > clipTop (we must make progress)
          let snapTo = -1;
          for (const bp of sectionBreaks) {
            if (bp > clipTop && bp <= proposedEnd && bp > snapTo) snapTo = bp;
          }
          if (snapTo >= 0) {
            const snappedVisible = snapTo - clipTop;
            const minFill = Math.floor(pageAvailableHeight * MIN_PAGE_FILL_TO_SNAP);
            if (snappedVisible >= minFill || currentPage.usedHeight + snappedVisible >= pageAvailableHeight * 0.6) {
              visibleOnThisPage = snappedVisible;
            }
          }
        }

        // If no space on this page (due to offset), move to next page
        if (visibleOnThisPage <= 0 && isFirst) {
          pages.push(currentPage);
          currentPage = {
            pageNumber: pages.length + 1,
            sections: [],
            usedHeight: 0
          };
          continue;
        }

        currentPage.sections.push({
          sectionId: section.id,
          offsetY: currentPage.usedHeight + offsetForThisPortion,
          height: sectionHeight,
          visibleHeight: visibleOnThisPage,
          clipTop: clipTop,
          isContinuation: !isFirst,
          userOffset: isFirst ? userOffset : 0
        });

        currentPage.usedHeight += visibleOnThisPage + offsetForThisPortion;
        remainingHeight -= visibleOnThisPage;
        clipTop += visibleOnThisPage;
        isFirst = false;

        // If more content remains, start a new page
        if (remainingHeight > 0) {
          pages.push(currentPage);
          currentPage = {
            pageNumber: pages.length + 1,
            sections: [],
            usedHeight: 0
          };
        }
      }
    }
  }

  // Don't forget the last page
  if (currentPage.sections.length > 0) {
    pages.push(currentPage);
  }

  // Ensure at least one page exists
  if (pages.length === 0) {
    pages.push({ pageNumber: 1, sections: [], usedHeight: 0 });
  }

  return pages;
}

export function useTwoPhaseRender({
  sections,
  pageHeight,
  headerHeight = 80,
  pageBreaksAfter,
  pageBreaksBefore,
  sectionOffsets
}: UseTwoPhaseRenderProps): UseTwoPhaseRenderReturn {
  const [phase, setPhase] = useState<'measuring' | 'paginated'>('measuring');
  const [heights, setHeights] = useState<Map<string, number>>(new Map());
  const [pageAssignments, setPageAssignments] = useState<PageAssignment[]>([]);
  const measureRef = useRef<HTMLDivElement>(null);
  const measureTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const measureRafRef = useRef<number | null>(null);

  // Memoize offset keys for dependency tracking
  const offsetsKey = useMemo(() => {
    return Array.from(sectionOffsets.entries()).map(([k, v]) => `${k}:${v}`).join(',');
  }, [sectionOffsets]);

  // Measurement function
  const performMeasurement = useCallback(() => {
    if (!measureRef.current) return;

    const newHeights = new Map<string, number>();
    // Natural break points per section — used by calculatePageAssignments
    // to avoid mid-paragraph / mid-row cuts when splitting tall sections.
    const newBreakpoints = new Map<string, number[]>();

    sections.forEach(section => {
      if (!section.isVisible) return;

      const el = measureRef.current?.querySelector(`[data-section-id="${section.id}"]`);
      if (el) {
        const sectionRect = el.getBoundingClientRect();
        // Add some padding for margins between sections
        newHeights.set(section.id, Math.ceil(sectionRect.height) + 16);

        // Collect candidate break points:
        //   1. Top of each direct child of .section-content (paragraph / card / matrix wrapper)
        //   2. Top of each <tr> inside the section (so wide tables can break between rows)
        // Offsets are relative to the section's own top.
        const breaks = new Set<number>();
        breaks.add(0);
        const sectionTop = sectionRect.top;
        const contentRoot = el.querySelector('.section-content') || el;
        // Direct children
        for (const child of Array.from(contentRoot.children)) {
          const r = (child as HTMLElement).getBoundingClientRect();
          const rel = Math.round(r.top - sectionTop);
          if (rel > 0) breaks.add(rel);
        }
        // Recurse one level deeper for grouped layouts (e.g., space-y wrappers
        // that contain individual cards as their own children).
        for (const child of Array.from(contentRoot.children)) {
          for (const grandchild of Array.from(child.children)) {
            const r = (grandchild as HTMLElement).getBoundingClientRect();
            const rel = Math.round(r.top - sectionTop);
            if (rel > 0) breaks.add(rel);
          }
        }
        // Table rows are always natural break points
        for (const tr of Array.from(contentRoot.querySelectorAll('tbody tr'))) {
          const r = (tr as HTMLElement).getBoundingClientRect();
          const rel = Math.round(r.top - sectionTop);
          if (rel > 0) breaks.add(rel);
        }
        // Sort ascending
        newBreakpoints.set(section.id, Array.from(breaks).sort((a, b) => a - b));
      }
    });

    setHeights(newHeights);

    // Calculate page assignments
    const assignments = calculatePageAssignments(
      sections,
      newHeights,
      pageHeight,
      headerHeight,
      pageBreaksAfter,
      pageBreaksBefore,
      sectionOffsets,
      newBreakpoints
    );
    setPageAssignments(assignments);

    // Switch to paginated view
    setPhase('paginated');
  }, [sections, pageHeight, headerHeight, pageBreaksAfter, pageBreaksBefore, sectionOffsets]);

  // Trigger measurement when in measuring phase
  useLayoutEffect(() => {
    if (phase !== 'measuring') return;

    // Cancel any pending measurement — both the timeout AND the rAF chain it
    // spawns. The rAF chain must be cancellable: when `sections` changes
    // (e.g. async report data finishes loading and the placeholder
    // disclaimer-only list is replaced by the full list), this effect re-runs
    // with a fresh `performMeasurement` closure. If the previous timeout had
    // already fired, only its rAF chain is still pending — leaving it
    // uncancelled lets a STALE performMeasurement (closed over the old
    // 1-section list) run against the now-full DOM, measure only the
    // disclaimer, and flip phase→'paginated', which suppresses the correct
    // re-measurement. That produced a 1-page, disclaimer-only export.
    if (measureTimeoutRef.current) {
      clearTimeout(measureTimeoutRef.current);
    }
    if (measureRafRef.current !== null) {
      cancelAnimationFrame(measureRafRef.current);
      measureRafRef.current = null;
    }

    // Wait for content to render, then measure
    measureTimeoutRef.current = setTimeout(() => {
      measureRafRef.current = requestAnimationFrame(() => {
        measureRafRef.current = requestAnimationFrame(() => {
          measureRafRef.current = null;
          performMeasurement();
        });
      });
    }, 100);

    return () => {
      if (measureTimeoutRef.current) {
        clearTimeout(measureTimeoutRef.current);
      }
      if (measureRafRef.current !== null) {
        cancelAnimationFrame(measureRafRef.current);
        measureRafRef.current = null;
      }
    };
  }, [phase, performMeasurement]);

  // Re-measure when dependencies change
  const remeasure = useCallback(() => {
    setPhase('measuring');
  }, []);

  // Trigger remeasure when sections or settings change (but NOT for offset changes)
  useLayoutEffect(() => {
    if (phase === 'paginated') {
      remeasure();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections.map(s => `${s.id}-${s.isVisible}-${s.orderIndex}`).join(','), pageHeight, pageBreaksAfter.size, pageBreaksBefore.size]);

  // Recalculate page assignments when offsets change (without remeasuring)
  useLayoutEffect(() => {
    if (phase === 'paginated' && heights.size > 0) {
      const assignments = calculatePageAssignments(
        sections,
        heights,
        pageHeight,
        headerHeight,
        pageBreaksAfter,
        pageBreaksBefore,
        sectionOffsets
      );
      setPageAssignments(assignments);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offsetsKey]);

  return {
    phase,
    heights,
    pageAssignments,
    measureRef,
    remeasure
  };
}

export default useTwoPhaseRender;
